import type { MutationMeta, QueryMeta } from "@tanstack/react-query"
import { reportErrorMessage } from "#/utils/ErrorReporting"

// Type the react-query `meta` bag app-wide: a query/mutation may declare an `errorReport` for the
// global onError handler to send to Sentry. Standard react-query meta pattern.
declare module "@tanstack/query-core" {
  interface QueryMeta {
    errorReport?: { message: string; tags?: Record<string, string> }
  }
  interface MutationMeta {
    errorReport?: { message: string; tags?: Record<string, string> }
  }
}

function describeError(error: unknown): string {
  if (error instanceof Error) return `${error.name}: ${error.message}`
  try {
    return String(error)
  } catch {
    return "unserializable error"
  }
}

// Reports a failed query/mutation to Sentry if it declared an `errorReport` on its `meta`; no-ops
// otherwise. Feature-agnostic — the report's `message` is the Sentry title and grouping key, its tags
// are for filtering.
export function reportTaggedQueryError(error: unknown, meta: QueryMeta | MutationMeta | undefined): void {
  const report = meta?.errorReport
  // Never let a malformed report throw out of the cache onError and crash the app — skip if there's
  // no message to report.
  if (!report?.message) return
  reportErrorMessage(report.message, {
    level: "error",
    fingerprint: [report.message],
    tags: report.tags,
    extra: { error: describeError(error) }
  })
}
